import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AddResourcePermissionType, ConfigurationStatus } from '@prisma/client';
import { Form, Formik } from 'formik';

import { formatEnumLabel } from 'src/utils/utils';

import { CustomInput } from '../CustomInput';
import { DropDown, FileUploadSelect } from '../FormikComponents';
import { TextAreaInput } from '../index';
import ResourceUploader from '../ResourceUploader';
import { showErrorToast } from '../Toasts/toast';

const ResourceForm = ({
    initialValues,
    validationSchema,
    onSubmit,
    onCancel,
    isEditMode = false,
    sectionsOptions,
    resourceTypeOptions,
}) => {
    const theme = useTheme();
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ values, errors, setFieldValue, handleSubmit, setTouched }) => {
                return (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={2} padding="16px 8px">
                            <CustomInput
                                name="resource_name"
                                label="Resource Name"
                                placeholder="Enter Resource Name..."
                                onChange={(event) => {
                                    setFieldValue('resource_name', event.target.value);
                                }}
                                boxStyling={{ width: '70%' }}
                                value={values.resource_name}
                                error={errors.resource_name}
                            />

                            {isEditMode && (
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Grid item>
                                        <FormLabel sx={{ color: theme.palette.common.black }}>
                                            Status
                                        </FormLabel>
                                    </Grid>
                                    <Grid item>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                aria-labelledby="status"
                                                defaultValue={
                                                    values?.status ?? ConfigurationStatus?.ACTIVE
                                                }
                                                name="status"
                                                row
                                                onChange={(event) => {
                                                    setFieldValue('status', event.target.value);
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={ConfigurationStatus.ACTIVE}
                                                    control={<Radio />}
                                                    label={formatEnumLabel(
                                                        ConfigurationStatus.ACTIVE
                                                    )}
                                                />
                                                <FormControlLabel
                                                    value={ConfigurationStatus.SUSPENDED}
                                                    control={<Radio />}
                                                    label={formatEnumLabel(
                                                        ConfigurationStatus.SUSPENDED
                                                    )}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Box>
                            )}

                            <Box display="flex" alignItems="center" gap={2}>
                                <Grid item>
                                    <FormLabel sx={{ color: theme.palette.common.black }}>
                                        View Access Permissions
                                    </FormLabel>
                                </Grid>
                                <Grid item>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-labelledby="view-access-permissions"
                                            defaultValue={
                                                values.viewAccessPermissions ??
                                                AddResourcePermissionType.ALL_PARTNERS
                                            }
                                            name="viewAccessPermissions"
                                            row
                                            onChange={(event) => {
                                                setFieldValue(
                                                    'viewAccessPermissions',
                                                    event.target.value
                                                );
                                            }}
                                        >
                                            {Object.values(AddResourcePermissionType).map(
                                                (value) => (
                                                    <FormControlLabel
                                                        key={value}
                                                        value={value}
                                                        control={<Radio />}
                                                        label={formatEnumLabel(value)}
                                                    />
                                                )
                                            )}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Box>

                            <DropDown
                                name="section"
                                label="Section"
                                placeholder="Select Section..."
                                options={sectionsOptions}
                                boxStyling={{ maxWidth: '100%' }}
                            />

                            <DropDown
                                name="resource_type"
                                label="Resource Type"
                                placeholder="Select Resource Type..."
                                options={resourceTypeOptions}
                                boxStyling={{ maxWidth: '100%' }}
                            />

                            <TextAreaInput
                                name="description"
                                placeholder="Enter description"
                                label="Description"
                                minRows={4}
                                inputStyle={{ style: { height: '100px' } }}
                            />

                            <FileUploadSelect
                                name="imageUrl"
                                label="Upload Image"
                                error={errors?.imageUrl}
                            />

                            <ResourceUploader />

                            <Box display="flex" gap={2} justifyContent="flex-end">
                                <Button
                                    onClick={onCancel}
                                    variant="main"
                                    className="reset-btn btn-medium"
                                >
                                    Cancel
                                </Button>

                                {values.section && (
                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (Object.keys(errors).length > 0) {
                                                setTouched({
                                                    resource_name: true,
                                                    description: true,
                                                    resource_type: true,
                                                    resourceUrl: true,
                                                    imageUrl: true,
                                                    section: true,
                                                    viewAccessPermissions: true,
                                                });
                                                showErrorToast('Please fill in required fields');
                                            } else {
                                                handleSubmit();
                                            }
                                        }}
                                        variant="main"
                                    >
                                        Confirm
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ResourceForm;
